import { Button, DatePicker, Input, Select, TreeSelect } from "antd";
import { CalendarOutlined, FilterOutlined, PlusOutlined, SearchOutlined, TagOutlined, UserOutlined } from "@ant-design/icons";
import { Headset } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { mixGetV1 } from "../../services/mixin";
import { fetchCareStatuses } from "../../pages/RoomChat/chatLabels";

const { RangePicker } = DatePicker;

type PeriodType = "date" | "week" | "month" | "year";
/** Ngoài các mốc cố định còn có "range": tự chọn khoảng từ ngày - đến ngày */
type PeriodMode = PeriodType | "range";

interface StaffOption {
    label: string;
    value: number;
}

interface SourceGroup {
    id: number;
    name: string;
    connections: { id: number; name: string }[];
}

interface InitialFilters {
    search: string;
    staffId?: number;
    pageStaffId?: number;
    period: string;
    refDate?: string;
    startDate?: string;
    endDate?: string;
    oauthAccountIds: number[];
    channelConnectionIds: number[];
    careStatusIds: number[];
}

const PERIOD_OPTIONS: { value: PeriodMode; label: string }[] = [
    { value: "date", label: "Ngày" },
    { value: "week", label: "Tuần" },
    { value: "month", label: "Tháng" },
    { value: "year", label: "Năm" },
    { value: "range", label: "Khoảng" },
];

const listOf = (res: any): any[] =>
    Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : [];

/** Nhân viên theo phòng ban: 2 = Trực page, 3 = Telesale */
const fetchStaffs = (typeDepartmentId: number) =>
    mixGetV1("/api/v1/be/staffs", { type_user: 1, limit: 200, type_department_id: typeDepartmentId })
        .then((res) => listOf(res)
            .filter((s: any) => s?.id)
            .map((s: any) => ({ label: s?.fullname || s?.name || `#${s.id}`, value: Number(s.id) })));

export default function CustomerToolbar({ onCreate, showCreate = true, lockStaff = false, expanded = true, extra, onSearch, onStaffChange, onPageStaffChange, onDateChange, onSourceChange, onCareStatusChange, initialFilters }: { onCreate: () => void; showCreate?: boolean; /** Telesales chỉ xem khách của mình: khoá ô Telesales phụ trách */ lockStaff?: boolean; /** false: chỉ hiện ô tìm kiếm, ẩn các bộ lọc còn lại */ expanded?: boolean; /** Nút đặt ngay cạnh ô tìm kiếm (vd: nút mở/thu bộ lọc) */ extra?: ReactNode; onSearch?: (value: string) => void; onStaffChange?: (staffId?: number) => void; onPageStaffChange?: (staffId?: number) => void; onDateChange?: (startDate?: string, endDate?: string, period?: string, refDate?: string) => void; onSourceChange?: (oauthAccountIds: number[], channelConnectionIds: number[]) => void; onCareStatusChange?: (careStatusIds: number[]) => void; initialFilters?: InitialFilters }) {
    const { t } = useTranslation();
    const [careStatusOptions, setCareStatusOptions] = useState<{ label: string; value: number; color?: string }[]>([]);
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [pageStaffOptions, setPageStaffOptions] = useState<StaffOption[]>([]);
    const [sourceGroups, setSourceGroups] = useState<SourceGroup[]>([]);
    const [loading, setLoading] = useState({ staff: false, pageStaff: false, source: false, care: false });
    const [staffId, setStaffId] = useState<number | undefined>(initialFilters?.staffId);
    const [pageStaffId, setPageStaffId] = useState<number | undefined>(initialFilters?.pageStaffId);
    const [careIds, setCareIds] = useState<number[]>(initialFilters?.careStatusIds || []);
    // Dựng lại các control theo bộ lọc khôi phục từ URL (quay lại từ trang chi tiết)
    const [selectedSources, setSelectedSources] = useState<string[]>(() => [
        ...(initialFilters?.oauthAccountIds || []).map((id) => `acc:${id}`),
        ...(initialFilters?.channelConnectionIds || []).map((id) => `conn:${id}`),
    ]);
    const [periodType, setPeriodType] = useState<PeriodMode>((initialFilters?.period as PeriodMode) || "date");
    const [refDate, setRefDate] = useState<Dayjs | null>(
        initialFilters?.refDate ? dayjs(initialFilters.refDate) : null
    );
    // Khoảng tự chọn (từ ngày - đến ngày), khôi phục lại từ URL khi quay lại danh sách
    const [rangeValue, setRangeValue] = useState<[Dayjs | null, Dayjs | null] | null>(
        initialFilters?.period === "range" && initialFilters?.startDate && initialFilters?.endDate
            ? [dayjs(initialFilters.startDate), dayjs(initialFilters.endDate)]
            : null
    );

    useEffect(() => {
        let mounted = true;
        const load = <T,>(key: keyof typeof loading, p: Promise<T>, apply: (v: T) => void) => {
            setLoading((l) => ({ ...l, [key]: true }));
            p.then((v) => { if (mounted) apply(v); })
                .catch(() => undefined)
                .finally(() => { if (mounted) setLoading((l) => ({ ...l, [key]: false })); });
        };
        load("staff", fetchStaffs(3), setStaffOptions);
        load("pageStaff", fetchStaffs(2), setPageStaffOptions);
        load("source", mixGetV1("/api/v1/be/oauth-accounts"), (res) => setSourceGroups(
            listOf(res)
                .filter((acc: any) => acc?.id)
                .map((acc: any) => ({
                    id: Number(acc.id),
                    name: acc?.provider || `#${acc.id}`,
                    connections: (Array.isArray(acc?.connections) ? acc.connections : [])
                        .filter((conn: any) => conn?.id)
                        .map((conn: any) => ({ id: Number(conn.id), name: conn?.display_name || conn?.external_id || `#${conn.id}` })),
                }))
        ));
        // Dùng lại cache trạng thái chăm sóc của phòng chat, tránh gọi lại API
        load("care", fetchCareStatuses(), (items) => setCareStatusOptions(items.map((s) => ({ label: s.name, value: s.id, color: s.color }))));
        return () => { mounted = false; };
    }, []);

    const emitDateRange = (type: PeriodType, date: Dayjs | null) => {
        if (!date) {
            onDateChange?.(undefined, undefined, type, undefined);
            return;
        }
        onDateChange?.(date.startOf(type).format("YYYY-MM-DD"), date.endOf(type).format("YYYY-MM-DD"), type, date.format("YYYY-MM-DD"));
    };

    const handlePeriodTypeChange = (value: PeriodMode) => {
        setPeriodType(value);
        if (value === "range") {
            // Chuyển sang khoảng tự chọn: dùng lại khoảng đã chọn trước đó nếu có
            const [start, end] = rangeValue || [];
            onDateChange?.(start && end ? start.format("YYYY-MM-DD") : undefined, start && end ? end.format("YYYY-MM-DD") : undefined, "range", undefined);
            return;
        }
        emitDateRange(value, refDate);
    };

    const handleRefDateChange = (date: Dayjs | null) => {
        setRefDate(date);
        emitDateRange(periodType as PeriodType, date);
    };

    const handleRangeChange = (dates: any) => {
        const start = dates?.[0] as Dayjs | undefined;
        const end = dates?.[1] as Dayjs | undefined;
        setRangeValue(dates ? [start || null, end || null] : null);
        onDateChange?.(start && end ? start.format("YYYY-MM-DD") : undefined, start && end ? end.format("YYYY-MM-DD") : undefined, "range", undefined);
    };

    const handleSourceChange = (values: string[]) => {
        setSelectedSources(values);
        const oauthAccountIds: number[] = [];
        const channelConnectionIds: number[] = [];
        values.forEach((val) => {
            const [type, rawId] = val.split(":");
            const id = Number(rawId);
            if (!id) return;
            if (type === "acc") oauthAccountIds.push(id);
            else if (type === "conn") channelConnectionIds.push(id);
        });
        onSourceChange?.(oauthAccountIds, channelConnectionIds);
    };

    const sourceTreeData = sourceGroups.map((group) => ({
        title: group.name,
        value: `acc:${group.id}`,
        key: `acc:${group.id}`,
        children: group.connections.map((conn) => ({ title: conn.name, value: `conn:${conn.id}`, key: `conn:${conn.id}` })),
    }));

    const selectProps = { variant: "borderless" as const, allowClear: true, showSearch: true, optionFilterProp: "label", className: "w-full" };

    return (
        <div className={`toolbar-customer flex flex-wrap items-center gap-2 ${showCreate ? "mb-4" : ""}`}>
            {/* Tìm kiếm (luôn hiện) + nút phụ */}
            {/* Ô tìm kiếm giãn chiếm phần trống: thu gọn thì kéo hết hàng; bung hết thì ô lọc cuối thẳng mép phải */}
            <div className={`flex w-full items-center gap-2 ${expanded ? "md:w-auto md:min-w-[220px] md:flex-1" : ""}`}>
            <div className="toolbar-item min-w-0 flex-1">
                <SearchOutlined className="icon" />
                <Input
                    variant="borderless"
                    placeholder={t('customer.search_placeholder')}
                    defaultValue={initialFilters?.search}
                    onChange={(e) => onSearch?.(e.target.value)}
                    allowClear
                />
            </div>
            {extra}
            </div>

            {expanded && (<>
            {/* Telesales phụ trách */}
            <div className="toolbar-item w-full sm:w-[180px]">
                <Headset size={14} className="icon shrink-0" />
                <Select
                    {...selectProps}
                    placeholder="Telesales phụ trách"
                    value={staffId}
                    disabled={lockStaff}
                    allowClear={!lockStaff}
                    onChange={(v) => { setStaffId(v); onStaffChange?.(v); }}
                    options={staffOptions}
                    loading={loading.staff}
                />
            </div>

            {/* Trực page tạo khách */}
            <div className="toolbar-item w-full sm:w-[170px]">
                <UserOutlined className="icon" />
                <Select
                    {...selectProps}
                    placeholder="Trực page tạo"
                    value={pageStaffId}
                    onChange={(v) => { setPageStaffId(v); onPageStaffChange?.(v); }}
                    options={pageStaffOptions}
                    loading={loading.pageStaff}
                />
            </div>

            {/* Ngày tạo */}
            <div className="toolbar-item w-full sm:w-auto">
                <CalendarOutlined className="icon" />
                <Select
                    variant="borderless"
                    value={periodType}
                    onChange={handlePeriodTypeChange}
                    options={PERIOD_OPTIONS}
                    popupMatchSelectWidth={false}
                    style={{ width: 80, flex: "none" }}
                />
                {periodType === "range" ? (
                    <RangePicker
                        variant="borderless"
                        value={rangeValue as any}
                        onChange={handleRangeChange}
                        format="DD/MM/YYYY"
                        placeholder={["Từ ngày", "Đến ngày"]}
                        style={{ width: 220 }}
                    />
                ) : (
                    <DatePicker
                        variant="borderless"
                        picker={periodType === "date" ? undefined : periodType}
                        value={refDate}
                        onChange={handleRefDateChange}
                        format={periodType === "date" ? "DD/MM/YYYY" : periodType === "month" ? "MM/YYYY" : periodType === "year" ? "YYYY" : undefined}
                        placeholder="Ngày tạo"
                        style={{ width: 130 }}
                    />
                )}
            </div>

            {/* Nguồn */}
            <div className="toolbar-item w-full md:w-[200px]">
                <FilterOutlined className="icon" />
                <TreeSelect
                    treeData={sourceTreeData}
                    value={selectedSources}
                    onChange={handleSourceChange}
                    treeCheckable
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    treeDefaultExpandAll
                    allowClear
                    variant="borderless"
                    className="w-full"
                    placeholder={t('customer.select_source')}
                    maxTagCount="responsive"
                    loading={loading.source}
                    treeNodeFilterProp="title"
                    showSearch
                />
            </div>

            {/* Trạng thái chăm sóc */}
            <div className="toolbar-item w-full md:w-[190px]">
                <TagOutlined className="icon" />
                <Select
                    {...selectProps}
                    mode="multiple"
                    maxTagCount="responsive"
                    placeholder="Thẻ chat"
                    value={careIds}
                    onChange={(v: number[]) => { setCareIds(v); onCareStatusChange?.(v); }}
                    options={careStatusOptions}
                    loading={loading.care}
                />
            </div>

            </>)}

            {showCreate && <Button type="primary" icon={<PlusOutlined />} onClick={onCreate} className="ml-auto h-[36px] !bg-[#3e91ab] font-semibold">
                Thêm khách
            </Button>}
        </div>
    );
}
